import React from "react";

type ErrorBoundaryProps = {
	children: React.ReactNode;
};

type ErrorBoundaryState = {
	hasError: boolean;
	error?: Error;
	errorInfo?: React.ErrorInfo;
	showDetails: boolean;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, showDetails: false };
	}

	static getDerivedStateFromError(error: Error) {
		// Don't catch navigation/routing errors or 404s
		// These should be handled by Next.js routing system
		const digest = (error as any).digest as string | undefined;
		if (
			error.message?.includes("404") ||
			error.message?.includes("Not Found") ||
			error.message?.includes("ChunkLoadError") ||
			error.name === "ChunkLoadError" ||
			digest?.includes("404")
		) {
			// Let Next.js handle these errors normally
			return { hasError: false };
		}

		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		// Don't handle navigation/routing errors
		const digest = (error as any).digest as string | undefined;
		if (
			error.message?.includes("404") ||
			error.message?.includes("Not Found") ||
			error.message?.includes("ChunkLoadError") ||
			error.name === "ChunkLoadError" ||
			digest?.includes("404")
		) {
			// Log but don't show error boundary UI
			console.warn("Navigation/routing error (not handled by ErrorBoundary):", error);
			return;
		}

		this.setState({ error, errorInfo });
		// Optionally log error to an external service
		console.error("ErrorBoundary caught an error:", error, errorInfo);
	}

	handleReport = () => {
		const { error } = this.state;
		const subject = encodeURIComponent("App Error Report");
		const body = encodeURIComponent(`Error: ${error?.message || "Unknown error"}

URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}

Please check the browser console for full stack trace details.`);
		const issueLink = `mailto:support@stackyhub.com?subject=${subject}&body=${body}`;
		window.open(issueLink, "_blank");
	};

	handleRetry = () => {
		this.setState({
			hasError: false,
			error: undefined,
			errorInfo: undefined,
			showDetails: false,
		});
		// Force a re-render by reloading the page
		window.location.reload();
	};

	handleGoBack = () => {
		// Check if there's history to go back to
		if (window.history.length > 1) {
			window.history.back();
		} else {
			// If no history, go to home page
			window.location.href = "/";
		}
	};

	toggleDetails = () => {
		this.setState((prev) => ({ showDetails: !prev.showDetails }));
	};

	render() {
		if (this.state.hasError) {
			const { error, errorInfo, showDetails } = this.state;

			return (
				<div
					style={{
						minHeight: "100vh",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "#f8f9fa",
						fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
					}}
				>
					<div
						style={{
							maxWidth: "600px",
							padding: "2rem",
							backgroundColor: "white",
							borderRadius: "8px",
							boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
							textAlign: "center",
						}}
					>
						<div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⚠️</div>

						<h1
							style={{
								color: "#dc3545",
								marginBottom: "1rem",
								fontSize: "1.5rem",
							}}
						>
							Oops! Something went wrong
						</h1>

						<p
							style={{
								color: "#6c757d",
								marginBottom: "1.5rem",
								lineHeight: "1.5",
							}}
						>
							We encountered an unexpected error. Please send us a report so we can fix this issue. Please also include the stack trace
							details if possible.
						</p>

						{error && (
							<div
								style={{
									backgroundColor: "#f8d7da",
									border: "1px solid #f5c6cb",
									borderRadius: "4px",
									padding: "1rem",
									marginBottom: "1.5rem",
									textAlign: "left",
								}}
							>
								<strong style={{ color: "#721c24" }}>Error:</strong>
								<div
									style={{
										color: "#721c24",
										fontFamily: "monospace",
										fontSize: "0.9rem",
										marginTop: "0.5rem",
									}}
								>
									{error.message}
								</div>
							</div>
						)}

						<div
							style={{
								display: "flex",
								gap: "1rem",
								justifyContent: "center",
								flexWrap: "wrap",
								marginBottom: "1rem",
							}}
						>
							<button
								onClick={this.handleGoBack}
								style={{
									backgroundColor: "#6c757d",
									color: "white",
									border: "none",
									padding: "0.75rem 1.5rem",
									borderRadius: "4px",
									cursor: "pointer",
									fontSize: "1rem",
									fontWeight: "500",
								}}
								onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#5a6268")}
								onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#6c757d")}
							>
								← Go Back
							</button>

							<button
								onClick={this.handleRetry}
								style={{
									backgroundColor: "#007bff",
									color: "white",
									border: "none",
									padding: "0.75rem 1.5rem",
									borderRadius: "4px",
									cursor: "pointer",
									fontSize: "1rem",
									fontWeight: "500",
								}}
								onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#0056b3")}
								onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#007bff")}
							>
								Try Again
							</button>

							<button
								onClick={this.handleReport}
								style={{
									backgroundColor: "#28a745",
									color: "white",
									border: "none",
									padding: "0.75rem 1.5rem",
									borderRadius: "4px",
									cursor: "pointer",
									fontSize: "1rem",
									fontWeight: "500",
								}}
								onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1e7e34")}
								onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#28a745")}
							>
								Report Issue
							</button>
						</div>

						<button
							onClick={this.toggleDetails}
							style={{
								backgroundColor: "transparent",
								color: "#6c757d",
								border: "1px solid #dee2e6",
								padding: "0.5rem 1rem",
								borderRadius: "4px",
								cursor: "pointer",
								fontSize: "0.9rem",
							}}
						>
							{showDetails ? "Hide" : "Show"} Technical Details
						</button>

						{showDetails && (error || errorInfo) && (
							<div
								style={{
									marginTop: "1.5rem",
									padding: "1rem",
									backgroundColor: "#f8f9fa",
									borderRadius: "4px",
									textAlign: "left",
									fontSize: "0.8rem",
									fontFamily: "monospace",
									maxHeight: "300px",
									overflow: "auto",
									border: "1px solid #dee2e6",
								}}
							>
								{error?.stack && (
									<div style={{ marginBottom: "1rem" }}>
										<strong>Stack Trace:</strong>
										<pre
											style={{
												whiteSpace: "pre-wrap",
												margin: "0.5rem 0 0 0",
												color: "#495057",
											}}
										>
											{error.stack}
										</pre>
									</div>
								)}

								{errorInfo?.componentStack && (
									<div>
										<strong>Component Stack:</strong>
										<pre
											style={{
												whiteSpace: "pre-wrap",
												margin: "0.5rem 0 0 0",
												color: "#495057",
											}}
										>
											{errorInfo.componentStack}
										</pre>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			);
		}
		return this.props.children;
	}
}
